<template>
  <div>
    <div class="card-9-53 mx-9 my-7">
      <!-- Header Section -->
      <div class="flex justify-between items-center border-b border-gray-100 px-5 pt-2.5 pb-2.5 m-0">
        <div>
          <h2 class="text-xl roboto-regular text-gray-800">Manajemen Kategori Topic</h2>
        </div>
        <div class="flex items-center space-x-2">
          <el-button 
            type="default" 
            icon="el-icon-back" 
            size="small" 
            @click="$router.push('/topic-management')"
            class="roboto-regular">
            Kembali ke Topic
          </el-button>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="get_categories_error" class="mx-5 my-3">
        <el-alert
          :title="get_categories_error"
          type="error"
          show-icon
          :closable="false">
        </el-alert>
      </div>

      <!-- Loading State -->
      <div v-if="get_categories_loading" class="loading-container">
        <div class="loader"></div>
      </div>

      <!-- Data Table -->
      <div v-if="!get_categories_loading">
        <el-table 
          :data="get_categories" 
          style="width: 100%"
          class="roboto-regular"
          :header-cell-style="{ backgroundColor: '#f5f7fa', color: '#606266' }">
          
          <el-table-column prop="category" label="Nama Kategori" min-width="250">
            <template slot-scope="scope">
              <div class="roboto-medium text-base">{{ scope.row.category || 'Uncategorized' }}</div>
            </template>
          </el-table-column>

          <el-table-column prop="topicCount" label="Jumlah Topic" width="150" align="center">
            <template slot-scope="scope">
              <el-tag type="primary" size="medium">
                {{ scope.row.topicCount || 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="subscriberCount" label="Total Subscribers" width="180" align="center">
            <template slot-scope="scope">
              <el-tag type="success" size="medium">
                {{ scope.row.subscriberCount || 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Aksi" width="200" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button 
                v-if="scope.row.category"
                size="mini" 
                type="warning" 
                icon="el-icon-edit"
                @click="renameCategory(scope.row)"
                plain>
                Rename
              </el-button>
              <el-button 
                v-if="scope.row.category"
                size="mini" 
                type="danger" 
                icon="el-icon-delete"
                @click="confirmDeleteCategory(scope.row)"
                plain>
                Hapus
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="px-5 py-4 border-t border-gray-100">
          <p class="text-sm text-gray-600">
            Total Kategori: <strong>{{ get_categories.length }}</strong>
          </p>
        </div>
      </div>
    </div>

    <!-- Rename Category Dialog -->
    <el-dialog 
      title="Rename Kategori"
      :visible.sync="showRenameDialog"
      width="500px">
      <el-form 
        :model="renameForm" 
        :rules="renameRules" 
        ref="renameForm"
        label-width="140px">
        
        <el-form-item label="Kategori Lama">
          <el-input 
            :value="selectedCategory ? selectedCategory.category : ''" 
            disabled>
          </el-input>
        </el-form-item>

        <el-form-item label="Kategori Baru" prop="newCategory">
          <el-input 
            v-model="renameForm.newCategory" 
            placeholder="Masukkan nama kategori baru">
          </el-input>
        </el-form-item>

        <el-alert
          title="Perhatian"
          type="warning"
          :closable="false"
          class="mb-3">
          <template slot="description">
            Semua topic dengan kategori "{{ selectedCategory ? selectedCategory.category : '' }}" 
            ({{ selectedCategory ? selectedCategory.topicCount : 0 }} topic) 
            akan dipindahkan ke kategori baru.
          </template>
        </el-alert>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showRenameDialog = false">Batal</el-button>
        <el-button 
          type="primary" 
          @click="submitRename"
          :loading="submitting">
          Rename
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'CategoryManagementView',
  data() {
    return {
      showRenameDialog: false,
      submitting: false,
      selectedCategory: null,
      renameForm: {
        newCategory: ''
      },
      renameRules: {
        newCategory: [
          { required: true, message: 'Nama kategori baru harus diisi', trigger: 'blur' },
          { min: 2, max: 50, message: 'Panjang 2-50 karakter', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState({
      get_categories: state => state.topic.categories,
      get_categories_loading: state => state.topic.loading,
      get_categories_error: state => state.topic.error
    })
  },
  methods: {
    ...mapActions('topic', [
      'getCategoriesWithStats',
      'renameCategory',
      'deleteCategory'
    ]),
    async loadCategories() {
      try {
        await this.getCategoriesWithStats()
      } catch (error) {
        this.$message.error('Gagal memuat data kategori')
      }
    },
    renameCategory(category) {
      this.selectedCategory = category
      this.renameForm.newCategory = ''
      this.showRenameDialog = true
    },
    async submitRename() {
      this.$refs.renameForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        this.submitting = true
        try {
          await this.$store.dispatch('topic/renameCategory', {
            oldCategory: this.selectedCategory.category,
            newCategory: this.renameForm.newCategory
          })
          
          this.$message.success('Kategori berhasil direname')
          this.showRenameDialog = false
          await this.loadCategories()
        } catch (error) {
          this.$message.error('Gagal rename kategori')
        } finally {
          this.submitting = false
        }
      })
    },
    async confirmDeleteCategory(category) {
      try {
        await this.$confirm(
          `Apakah Anda yakin ingin menghapus kategori "${category.category}"? Semua topic (${category.topicCount} topic) dalam kategori ini akan menjadi uncategorized.`,
          'Konfirmasi Hapus',
          {
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
            type: 'warning'
          }
        )
        
        await this.deleteCategory(category.category)
        this.$message.success('Kategori berhasil dihapus')
        await this.loadCategories()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('Gagal menghapus kategori')
        }
      }
    }
  },
  mounted() {
    this.loadCategories()
  }
}
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.card-9-53 {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
